// src/components/admin/settings/GrossisteSettings.jsx
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import DataTableSettings from '../../common/DataTableSettings';
import ModalComponent from '../../common/ModalComponent';
import { AlertCircle, Building, Phone, MapPin, User, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  GET_GROSSISTES,
  CREATE_GROSSISTE,
  UPDATE_GROSSISTE,
  DELETE_GROSSISTE
} from '../../../api/grossiste-api';

const GrossisteSettings = () => {
  // State management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentGrossiste, setCurrentGrossiste] = useState(null);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    adresse: '',
    nomContact: '',
    prenomContact: '',
    telContact: '',
    piecesJointes: '',
    zoneCommentaire: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [fileUploading, setFileUploading] = useState(false);

  // GraphQL hooks
  const {
    loading: grossistesLoading,
    error: grossistesError,
    data: grossistesData,
    refetch: refetchGrossistes
  } = useQuery(GET_GROSSISTES, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  });

  const [createGrossiste, { loading: createLoading }] = useMutation(CREATE_GROSSISTE, {
    onCompleted: (data) => {
      if (data.createGrossiste) {
        toast.success('Grossiste créé avec succès');
        setIsModalOpen(false);
        refetchGrossistes();
        resetForm();
      }
    },
    onError: (error) => {
      console.error('Create grossiste error:', error);
      toast.error(`Erreur lors de la création: ${error.message}`);
    }
  });

  const [updateGrossiste, { loading: updateLoading }] = useMutation(UPDATE_GROSSISTE, {
    onCompleted: (data) => {
      if (data.updateGrossiste) {
        toast.success('Grossiste modifié avec succès');
        setIsModalOpen(false);
        refetchGrossistes();
        resetForm();
      }
    },
    onError: (error) => {
      console.error('Update grossiste error:', error);
      toast.error(`Erreur lors de la modification: ${error.message}`);
    }
  });

  const [deleteGrossiste, { loading: deleteLoading }] = useMutation(DELETE_GROSSISTE, {
    onCompleted: (data) => {
      if (data.removeGrossiste) {
        toast.success('Grossiste supprimé avec succès');
        setIsConfirmDeleteOpen(false);
        setCurrentGrossiste(null);
        refetchGrossistes();
      }
    },
    onError: (error) => {
      console.error('Delete grossiste error:', error);
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    }
  });

  // Handle GraphQL errors
  useEffect(() => {
    if (grossistesError) {
      console.error('Grossistes query error:', grossistesError);
      toast.error('Erreur lors du chargement des grossistes');
    }
  }, [grossistesError]);

  // Table columns configuration
  const columns = [
    {
      Header: 'Nom',
      accessor: 'nom',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <Building size={16} className="mr-2 text-blue-500" />
          <span className="font-medium">{value}</span>
        </div>
      )
    },
    {
      Header: 'Adresse',
      accessor: 'adresse',
      sortable: true,
      searchable: true,
      Cell: ({ value }) => (
        <div className="flex items-center">
          <MapPin size={16} className="mr-2 text-gray-400" />
          <span className="text-sm text-gray-600 max-w-xs truncate">{value}</span>
        </div>
      )
    },
    {
      Header: 'Contact',
      accessor: 'contact',
      sortable: false,
      searchable: true,
      Cell: ({ row }) => (
        <div>
          <div className="flex items-center">
            <User size={16} className="mr-2 text-green-500" />
            <span className="font-medium text-sm">
              {row.prenomContact} {row.nomContact}
            </span>
          </div>
          <div className="flex items-center mt-1">
            <Phone size={14} className="mr-2 text-gray-400" />
            <span className="text-xs text-gray-500">{row.telContact}</span>
          </div>
        </div>
      )
    },
    {
      Header: 'Documents',
      accessor: 'piecesJointes',
      sortable: false,
      searchable: false,
      Cell: ({ value }) => (
        <div className="flex items-center">
          {value ? (
            <>
              <FileText size={16} className="mr-1 text-green-600" />
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                Disponible
              </span>
            </>
          ) : (
            <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
              Aucun
            </span>
          )}
        </div>
      )
    },
    {
      Header: 'Date création',
      accessor: 'createdAt',
      sortable: true,
      searchable: false,
      Cell: ({ value }) => (
        <span className="text-sm text-gray-500">
          {format(new Date(value), 'dd MMM yyyy', { locale: fr })}
        </span>
      )
    }
  ];

  // Form handlers
  const resetForm = () => {
    setFormData({
      nom: '',
      adresse: '',
      nomContact: '',
      prenomContact: '',
      telContact: '',
      piecesJointes: '',
      zoneCommentaire: ''
    });
    setFormErrors({});
    setCurrentGrossiste(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setIsModalOpen(true);
  };

  const handleEdit = (grossiste) => {
    setCurrentGrossiste(grossiste);
    setFormData({
      nom: grossiste.nom || '',
      adresse: grossiste.adresse || '',
      nomContact: grossiste.nomContact || '',
      prenomContact: grossiste.prenomContact || '',
      telContact: grossiste.telContact || '',
      piecesJointes: grossiste.piecesJointes || '',
      zoneCommentaire: grossiste.zoneCommentaire || ''
    });
    setFormErrors({});
    setIsModalOpen(true);
  };

  const handleDelete = (grossiste) => {
    setCurrentGrossiste(grossiste);
    setIsConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (currentGrossiste) {
      await deleteGrossiste({
        variables: { id: currentGrossiste.id }
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is filled
    if (value && formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Le fichier ne doit pas dépasser 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      toast.error('Seuls les fichiers PDF et DOCX sont autorisés');
      return;
    }

    setFileUploading(true);
    
    try {
      // In a real application, you would upload the file to a server
      // For now, we'll just store the filename
      setFormData(prev => ({
        ...prev,
        piecesJointes: file.name
      }));
      
      toast.success('Fichier ajouté avec succès');
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Erreur lors de l\'upload du fichier');
    } finally {
      setFileUploading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = 'Le nom du grossiste est requis';
    }
    
    if (!formData.adresse.trim()) {
      errors.adresse = 'L\'adresse est requise';
    }
    
    if (!formData.nomContact.trim()) {
      errors.nomContact = 'Le nom du contact est requis';
    }
    
    if (!formData.prenomContact.trim()) {
      errors.prenomContact = 'Le prénom du contact est requis';
    }
    
    if (!formData.telContact.trim()) {
      errors.telContact = 'Le téléphone du contact est requis';
    } else if (!/^(\+212|0)[5-7][0-9]{8}$/.test(formData.telContact.replace(/\s/g, ''))) {
      errors.telContact = 'Format de téléphone invalide (ex: +212 5XX XXX XXX)';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    const grossisteData = {
      nom: formData.nom.trim(),
      adresse: formData.adresse.trim(),
      nomContact: formData.nomContact.trim(),
      prenomContact: formData.prenomContact.trim(),
      telContact: formData.telContact.trim(),
      piecesJointes: formData.piecesJointes || null,
      zoneCommentaire: formData.zoneCommentaire.trim() || null
    };
    
    try {
      if (currentGrossiste) {
        await updateGrossiste({
          variables: {
            updateGrossisteInput: {
              id: currentGrossiste.id,
              ...grossisteData
            }
          }
        });
      } else {
        await createGrossiste({
          variables: {
            createGrossisteInput: grossisteData
          }
        });
      }
    } catch (error) {
      // Error handling is done in the mutation onError callbacks
      console.error('Submit error:', error);
    }
  };

  const isSubmitting = createLoading || updateLoading;

  return (
    <div>
      <DataTableSettings
        title="Gestion des Grossistes"
        subtitle="Gérez les grossistes partenaires de la plateforme"
        data={grossistesData?.grossistes || []}
        columns={columns}
        loading={grossistesLoading}
        error={grossistesError}
        onCreate={handleCreateNew}
        onEdit={handleEdit}
        onDelete={handleDelete}
        emptyStateProps={{
          icon: Building,
          title: "Aucun grossiste",
          description: "Commencez par créer votre premier grossiste partenaire."
        }}
      />
      
      {/* Create/Edit Modal */}
      <ModalComponent
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title={currentGrossiste ? 'Modifier le grossiste' : 'Créer un nouveau grossiste'}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        size="lg"
        submitLabel={currentGrossiste ? 'Modifier' : 'Créer'}
      >
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <Building className="w-4 h-4 mr-2" />
              Informations générales
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du grossiste <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    formErrors.nom ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="MediDistribution"
                />
                {formErrors.nom && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.nom}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="adresse"
                  value={formData.adresse}
                  onChange={handleInputChange}
                  rows={2}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    formErrors.adresse ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="123 Avenue Centrale, Casablanca"
                />
                {formErrors.adresse && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.adresse}</p>
                )}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Informations de contact
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom du contact <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nomContact"
                  value={formData.nomContact}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    formErrors.nomContact ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Benali"
                />
                {formErrors.nomContact && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.nomContact}</p>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prénom du contact <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="prenomContact"
                  value={formData.prenomContact}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                    formErrors.prenomContact ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Ahmed"
                />
                {formErrors.prenomContact && (
                  <p className="mt-1 text-sm text-red-600">{formErrors.prenomContact}</p>
                )}
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone du contact <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="telContact"
                value={formData.telContact}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  formErrors.telContact ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="0522123456"
              />
              {formErrors.telContact && (
                <p className="mt-1 text-sm text-red-600">{formErrors.telContact}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Format: 0XXXXXXXXX
              </p>
            </div>
          </div>

          {/* Documents and Comments */}
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Documents et commentaires
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Documents annexes
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload-grossiste"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-green-700 hover:text-green-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500"
                      >
                        <span>
                          {fileUploading ? 'Upload en cours...' : 'Ajouter un fichier'}
                        </span>
                        <input
                          id="file-upload-grossiste"
                          name="piecesJointes"
                          type="file"
                          className="sr-only"
                          accept=".pdf,.docx,.doc"
                          onChange={handleFileChange}
                          disabled={fileUploading}
                        />
                      </label>
                      <p className="pl-1">ou glisser-déposer</p>
                    </div>
                    <p className="text-xs text-gray-500">PDF, DOCX jusqu'à 10MB</p>
                    {formData.piecesJointes && (
                      <div className="mt-2 p-2 bg-green-100 border border-green-200 rounded">
                        <p className="text-sm text-green-700 flex items-center">
                          <FileText size={14} className="mr-1" />
                          {formData.piecesJointes}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Commentaires additionnels
                </label>
                <textarea
                  name="zoneCommentaire"
                  value={formData.zoneCommentaire}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Informations supplémentaires sur le grossiste, conditions particulières, historique de collaboration..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Optionnel - Informations internes non visibles par le grossiste
                </p>
              </div>
            </div>
          </div>

          {/* Summary for edit mode */}
          {currentGrossiste && (
            <div className="bg-gray-100 p-3 rounded-lg">
              <p className="text-xs text-gray-600">
                <strong>Créé le:</strong> {format(new Date(currentGrossiste.createdAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
              </p>
              {currentGrossiste.updatedAt !== currentGrossiste.createdAt && (
                <p className="text-xs text-gray-600 mt-1">
                  <strong>Dernière modification:</strong> {format(new Date(currentGrossiste.updatedAt), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                </p>
              )}
            </div>
          )}
        </div>
      </ModalComponent>
      
      {/* Confirm Delete Modal */}
      <ModalComponent
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setCurrentGrossiste(null);
        }}
        title="Confirmer la suppression"
        onSubmit={confirmDelete}
        isSubmitting={deleteLoading}
        submitLabel="Supprimer"
        submitButtonClassName="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        size="md"
      >
        <div className="space-y-4">
          <div className="flex items-center space-x-3 text-red-600">
            <AlertCircle size={24} />
            <h3 className="text-lg font-medium">Attention</h3>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <p className="text-gray-700 mb-2">
              Êtes-vous sûr de vouloir supprimer le grossiste :
            </p>
            <div className="bg-white p-3 rounded border-l-4 border-red-500">
              <p className="font-semibold text-gray-900">{currentGrossiste?.nom}</p>
              <p className="text-sm text-gray-600">{currentGrossiste?.adresse}</p>
              <p className="text-sm text-gray-600">
                Contact: {currentGrossiste?.prenomContact} {currentGrossiste?.nomContact}
              </p>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-3 rounded border border-yellow-100">
            <p className="text-sm text-yellow-800">
              <strong>Cette action est irréversible.</strong> Toutes les données associées à ce grossiste seront définitivement supprimées.
            </p>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>Pour confirmer la suppression, cliquez sur le bouton "Supprimer" ci-dessous.</p>
          </div>
        </div>
      </ModalComponent>
    </div>
  );
};

export default GrossisteSettings;